if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.handleVisibleSlidesChange = this.handleVisibleSlidesChange.bind(this);
        this.handleDeferredMediaIntersection = this.handleDeferredMediaIntersection.bind(this);
        this.handleModalOpenIntent = this.handleModalOpenIntent.bind(this);
        this.handleModalTriggerClick = this.handleModalTriggerClick.bind(this);
        this.unlockInitialMediaSync = this.unlockInitialMediaSync.bind(this);
        this.isSyncingInitialMedia = false;
      }

      connectedCallback() {
        super.connectedCallback();
        this.content = this.querySelector('.product-media-modal__content');
        document.addEventListener('pointerdown', this.handleModalOpenIntent, { capture: true, passive: true });
        document.addEventListener('mouseover', this.handleModalOpenIntent, { capture: true, passive: true });
        document.addEventListener('click', this.handleModalTriggerClick, { capture: true });
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.handleModalOpenIntent, { capture: true });
        document.removeEventListener('mouseover', this.handleModalOpenIntent, { capture: true });
        document.removeEventListener('click', this.handleModalTriggerClick, { capture: true });
      }

      hide() {
        this.slideObserver?.disconnect();
        this.deferredMediaObserver?.disconnect();
        this.teardownInitialMediaSyncListeners();
        this.isSyncingInitialMedia = false;
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.content = this.querySelector('.product-media-modal__content');
        this.ensureModalContent().then((loaded) => {
          if (!loaded || !this.hasAttribute('open')) return;
          this.content = this.querySelector('.product-media-modal__content');
          this.isSyncingInitialMedia = true;
          this.showActiveMedia();
          this.observeSlides();
          this.observeDeferredMedia();
          this.setupInitialMediaSyncListeners();
        });
      }

      ensureModalContent() {
        if (this.dataset.productModalDeferred !== 'true' || this.dataset.productModalLoaded === 'true') {
          return Promise.resolve(true);
        }

        if (this.modalContentPromise) return this.modalContentPromise;

        const sectionId = this.dataset.section;
        if (!sectionId) return Promise.resolve(false);

        const requestUrl = new URL(this.dataset.productUrl || window.location.href, window.location.origin);
        requestUrl.searchParams.delete('section_id');
        requestUrl.searchParams.set('sections', sectionId);

        const currentVariantId = this.getCurrentVariantId();
        if (currentVariantId) requestUrl.searchParams.set('variant', currentVariantId);

        this.dataset.productModalLoading = 'true';
        this.modalContentPromise = fetch(requestUrl.toString(), { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`Unable to load product modal (${response.status})`);
            return response.text();
          })
          .then((payload) => this.extractModalMarkup(payload, sectionId))
          .then((markup) => {
            if (!markup) return false;

            const doc = new DOMParser().parseFromString(markup, 'text/html');
            const nextModal = doc.getElementById(this.id) || doc.querySelector('product-modal');
            if (!nextModal) return false;

            this.syncModalContent(nextModal);
            this.dataset.productModalLoaded = 'true';
            return true;
          })
          .catch(() => false)
          .finally(() => {
            delete this.dataset.productModalLoading;
            this.modalContentPromise = null;
          });

        return this.modalContentPromise;
      }

      handleModalOpenIntent(event) {
        const opener = event.target?.closest?.('[data-product-modal-trigger][data-modal], modal-opener[data-modal]');
        if (!opener || opener.getAttribute('data-modal') !== `#${this.id}`) return;
        this.ensureModalContent();
      }

      handleModalTriggerClick(event) {
        const trigger = event.target?.closest?.('[data-product-modal-trigger][data-modal]');
        if (!trigger || trigger.getAttribute('data-modal') !== `#${this.id}`) return;

        event.preventDefault();
        event.stopPropagation();
        this.show(trigger);
      }

      extractModalMarkup(payload, sectionId) {
        const trimmedPayload = String(payload || '').trim();
        if (!trimmedPayload.startsWith('{')) return payload;

        try {
          const sections = JSON.parse(trimmedPayload);
          return sections[sectionId] || Object.values(sections).find((markup) => typeof markup === 'string' && markup);
        } catch (error) {
          return payload;
        }
      }

      syncModalContent(nextModal) {
        const currentMap = this.querySelector('[data-variant-media-map]');
        const nextMap = nextModal.querySelector('[data-variant-media-map]');
        if (nextMap) {
          if (currentMap) {
            currentMap.textContent = nextMap.textContent;
          } else {
            this.querySelector('.product-media-modal__dialog')?.insertBefore(
              nextMap.cloneNode(true),
              this.querySelector('.product-media-modal__content')
            );
          }
        } else if (currentMap) {
          currentMap.remove();
        }

        const currentContent = this.querySelector('.product-media-modal__content');
        const nextContent = nextModal.querySelector('.product-media-modal__content');
        if (currentContent && nextContent) currentContent.innerHTML = nextContent.innerHTML;
      }

      showActiveMedia() {
        const container = this.querySelector('.product-media-modal__content');
        if (!container) return;

        const openedMediaId =
          this.openedBy?.getAttribute('data-modal-media-id') || this.openedBy?.getAttribute('data-media-id');
        if (!openedMediaId) return;

        const activeMedia = Array.from(container.children).find(
          (element) => this.extractMediaId(element.dataset.mediaId) === String(openedMediaId)
        );
        if (!activeMedia) return;

        this.setActiveMediaElement(activeMedia);
        container.scrollLeft = activeMedia.offsetLeft;
      }

      observeSlides() {
        const container = this.content || this.querySelector('.product-media-modal__content');
        if (!container) return;

        this.slideObserver?.disconnect();
        this.slideObserver = new IntersectionObserver(this.handleVisibleSlidesChange, {
          root: container,
          threshold: [0.55, 0.75],
        });

        Array.from(container.children)
          .filter((element) => element.hasAttribute('data-media-id') && !element.hasAttribute('hidden'))
          .forEach((element) => this.slideObserver.observe(element));
      }

      observeDeferredMedia() {
        const container = this.content || this.querySelector('.product-media-modal__content');
        if (!container || typeof IntersectionObserver === 'undefined') return;

        this.deferredMediaObserver?.disconnect();
        this.deferredMediaObserver = new IntersectionObserver(this.handleDeferredMediaIntersection, {
          root: container,
          rootMargin: '200px 0px',
        });

        this.querySelectorAll('deferred-media[data-media-id]').forEach((element) => {
          if (!element.hasAttribute('loaded')) {
            this.deferredMediaObserver.observe(element);
          }
        });
      }

      handleVisibleSlidesChange(entries) {
        if (this.isSyncingInitialMedia) return;

        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (!visibleEntries.length) return;

        const container = this.content || this.querySelector('.product-media-modal__content');
        const containerCenter = container ? container.scrollLeft + container.clientWidth / 2 : 0;
        const activeMedia = visibleEntries
          .sort((a, b) => {
            if (b.intersectionRatio !== a.intersectionRatio) return b.intersectionRatio - a.intersectionRatio;

            const aCenter = a.target.offsetLeft + a.target.clientWidth / 2;
            const bCenter = b.target.offsetLeft + b.target.clientWidth / 2;
            return Math.abs(aCenter - containerCenter) - Math.abs(bCenter - containerCenter);
          })[0]?.target;
        if (!activeMedia) return;
        this.setActiveMediaElement(activeMedia);
      }

      handleDeferredMediaIntersection(entries) {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const deferredMedia = entry.target;
          if (typeof deferredMedia.loadContent === 'function') {
            deferredMedia.loadContent(false);
          }
          this.deferredMediaObserver?.unobserve(deferredMedia);
        });
      }

      setupInitialMediaSyncListeners() {
        if (!this.content) return;
        this.teardownInitialMediaSyncListeners();
        this.content.addEventListener('pointerdown', this.unlockInitialMediaSync, { passive: true });
        this.content.addEventListener('touchstart', this.unlockInitialMediaSync, { passive: true });
        this.content.addEventListener('wheel', this.unlockInitialMediaSync, { passive: true });
      }

      teardownInitialMediaSyncListeners() {
        if (!this.content) return;
        this.content.removeEventListener('pointerdown', this.unlockInitialMediaSync);
        this.content.removeEventListener('touchstart', this.unlockInitialMediaSync);
        this.content.removeEventListener('wheel', this.unlockInitialMediaSync);
      }

      unlockInitialMediaSync() {
        this.isSyncingInitialMedia = false;
        this.teardownInitialMediaSyncListeners();
      }

      setActiveMediaElement(activeMedia) {
        this.querySelectorAll('[data-media-id].active').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('active');
        });

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        this.updateVariantLabel(activeMedia);
        this.updatePageCount(activeMedia);

        if (activeMedia.nodeName == 'DEFERRED-MEDIA' && typeof activeMedia.loadContent === 'function') {
          if (activeMediaContent && activeMediaContent.querySelector('.js-youtube')) {
            activeMedia.loadContent();
          } else {
            activeMedia.loadContent(false);
          }
        }
      }

      updateVariantLabel(activeMedia) {
        this.querySelectorAll('[data-product-media-modal-variant-label]').forEach((labelNode) => {
          labelNode.textContent = '';
          labelNode.hidden = true;
        });

        const labelEl = activeMedia?.querySelector('[data-product-media-modal-variant-label]');
        if (!labelEl) return;

        const mediaId = this.extractMediaId(activeMedia?.dataset.mediaId);
        const variantMediaData = this.getVariantMediaData();
        const mediaOptionLabels = variantMediaData?.mediaOptionLabels || {};
        const mediaOptionLabelUrls = variantMediaData?.mediaOptionLabelUrls || {};
        const variantOptionLabels = variantMediaData?.variantOptionLabels || {};
        const variantMedia = variantMediaData?.variantMedia || {};
        const currentVariantId = this.getCurrentVariantId();
        const currentVariantMediaIds = currentVariantId ? variantMedia[String(currentVariantId)] || [] : [];
        const activeMediaUrl = this.normalizeMediaUrl(this.getSlideMediaUrl(activeMedia));

        let label = mediaId ? mediaOptionLabels[String(mediaId)] || '' : '';
        if (!label && activeMediaUrl) {
          label = mediaOptionLabelUrls[activeMediaUrl] || '';
        }
        if (!label && mediaId && currentVariantId && currentVariantMediaIds.map(String).includes(String(mediaId))) {
          label = variantOptionLabels[String(currentVariantId)] || '';
        }
        if (!label && mediaId) {
          label = this.getMappedVariantLabelForMedia(mediaId, variantMedia, variantOptionLabels);
        }
        if (!label) {
          label = this.getCurrentVariantLabelFromPicker();
        }

        labelEl.textContent = label || '';
        labelEl.hidden = !label;
      }

      updatePageCount(activeMedia) {
        const pageCountEl = this.querySelector('[data-product-media-modal-page-count]');
        if (!pageCountEl) return;

        const container = this.content || this.querySelector('.product-media-modal__content');
        if (!container) return;

        const visibleSlides = Array.from(container.children).filter(
          (element) => element.hasAttribute('data-media-id') && !element.hasAttribute('hidden')
        );

        if (!visibleSlides.length) {
          pageCountEl.textContent = '';
          pageCountEl.hidden = true;
          return;
        }

        const activeIndex = visibleSlides.findIndex((element) => element === activeMedia);
        const currentPage = activeIndex >= 0 ? activeIndex + 1 : 1;

        pageCountEl.textContent = `${currentPage} / ${visibleSlides.length}`;
        pageCountEl.hidden = false;
      }

      getVariantMediaData() {
        const mapEl = this.querySelector('[data-variant-media-map]');
        if (!mapEl) return null;

        try {
          return JSON.parse(mapEl.textContent || '{}');
        } catch (error) {
          console.warn('Invalid variant media map JSON', error);
          return null;
        }
      }

      getCurrentVariantId() {
        return this.dataset.currentVariantId || null;
      }

      getSlideMediaUrl(slide) {
        if (!slide) return '';
        if (slide.matches?.('img[src], video[src], source[src], iframe[src]')) {
          return (
            slide.currentSrc ||
            slide.getAttribute('src') ||
            slide.src ||
            slide.getAttribute('poster') ||
            ''
          );
        }
        const mediaElement = slide.querySelector(
          '.product__modal-opener--image img[src], img[src], source[src], video[src], iframe[src]'
        );
        if (!mediaElement) return '';
        return (
          mediaElement.currentSrc ||
          mediaElement.getAttribute('src') ||
          mediaElement.src ||
          mediaElement.getAttribute('poster') ||
          ''
        );
      }

      normalizeMediaUrl(value) {
        if (!value) return '';
        let pathname = '';
        try {
          pathname = new URL(String(value), window.location.origin).pathname || '';
        } catch (error) {
          pathname = String(value)
            .split('#')[0]
            .split('?')[0]
            .replace(/^https?:\/\/[^/]+/i, '')
            .replace(/^\/\/[^/]+/i, '');
        }
        const filePath = pathname.split('/files/').pop();
        if (filePath && filePath !== pathname) return `files/${filePath}`;
        return (pathname || '').replace(/^\/+/, '');
      }

      getMappedVariantLabelForMedia(mediaId, variantMedia = {}, variantOptionLabels = {}) {
        if (!mediaId) return '';

        const mediaIdString = String(mediaId);
        for (const [variantId, mediaIds] of Object.entries(variantMedia)) {
          if (!Array.isArray(mediaIds)) continue;
          if (mediaIds.map(String).includes(mediaIdString)) {
            const label = variantOptionLabels[String(variantId)] || '';
            if (label) return label;
          }
        }

        return '';
      }

      getCurrentVariantLabelFromPicker() {
        const sectionId = this.dataset.section;
        if (!sectionId) return '';

        const variantSelects = document.querySelector(`#variant-selects-${sectionId}`);
        if (!variantSelects) return '';

        const configuredNames = (variantSelects.dataset.mediaUpdateOptionNames || '')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        const optionRows = Array.from(variantSelects.querySelectorAll('.product-form__input'));
        let firstSelectedValue = '';

        for (const row of optionRows) {
          const selectedValueEl = row.querySelector('[data-selected-value]');
          const selectedValue = selectedValueEl?.textContent?.trim() || '';
          if (!firstSelectedValue && selectedValue) firstSelectedValue = selectedValue;

          const legend = row.querySelector('legend.form__label');
          const optionName = legend?.childNodes?.[0]?.textContent?.replace(':', '').trim().toLowerCase() || '';
          if (selectedValue && optionName && configuredNames.includes(optionName)) {
            return selectedValue;
          }
        }

        return firstSelectedValue;
      }

      extractMediaId(value) {
        if (!value) return null;
        const parts = String(value).split('-');
        return parts[parts.length - 1] || null;
      }
    }
  );
}
